// src/.../components/user/SecondStep/AddAppointmentContentStep2Normal.jsx
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import moment from "moment-jalaali";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Lock,
  CheckCircle,
  Loader2,
  AlertCircle,
  User,
  Users,
  Clock,
  Clover,
} from "lucide-react";
import { toast } from "react-toastify";

// ============= ثابت‌ها =============
const persianMonths = [
  { id: 1, name: "فروردین", days: 31 },
  { id: 2, name: "اردیبهشت", days: 31 },
  { id: 3, name: "خرداد", days: 31 },
  { id: 4, name: "تیر", days: 31 },
  { id: 5, name: "مرداد", days: 31 },
  { id: 6, name: "شهریور", days: 31 },
  { id: 7, name: "مهر", days: 30 },
  { id: 8, name: "آبان", days: 30 },
  { id: 9, name: "آذر", days: 30 },
  { id: 10, name: "دی", days: 30 },
  { id: 11, name: "بهمن", days: 30 },
  { id: 12, name: "اسفند", days: 29 },
];

const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const reasonLabelMap = {
  SALON_HOLIDAY: "تعطیلی سالن",
  FULLY_BOOKED: "ظرفیت تکمیل",
  NO_STAFF: "هیچ نیرویی موجود نیست",
  NO_STAFF_WORKING: "هیچ نیرویی کار نمی‌کند",
};

const isLeapYear = (year) => {
  const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
  return breaks.includes(year % 33);
};

const getDaysInMonth = (month, year) => {
  if (!month || month < 1 || month > 12) return 30;
  if (month === 12 && isLeapYear(year)) return 30;
  return persianMonths[month - 1].days;
};

// ============= تابع انتخاب رندوم نیرو با اولویت کمترین قرار =============
const pickRandomStaffWithLowestLoad = (staffs) => {
  if (!staffs || staffs.length === 0) return null;
  if (staffs.length === 1) return staffs[0];

  // پیدا کردن کمترین appointmentCount
  const minCount = Math.min(...staffs.map((s) => s.appointmentCount ?? 0));
  // فیلتر کردن نیروهایی که کمترین قرار را دارند
  const leastLoaded = staffs.filter(
    (s) => (s.appointmentCount ?? 0) === minCount
  );
  // انتخاب رندوم از بین آن‌ها
  const randomIndex = Math.floor(Math.random() * leastLoaded.length);
  return leastLoaded[randomIndex];
};

// ============= تابع اضافه کردن زمان =============
const addTime = (timeStr, hoursToAdd, minutesToAdd) => {
  const [h, m] = timeStr.split(":").map(Number);
  let totalMinutes = h * 60 + m + hoursToAdd * 60 + minutesToAdd;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMinutes = totalMinutes % 60;
  return `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(2, "0")}`;
};

// ============= کامپوننت اصلی =============
const AddAppointmentContentStep2Normal = ({
  availabilityData,
  axiosAdmin,
  serviceId,
  type,
  onStepDataChange,
  internalSelectedDate,
  setInternalSelectedDate,
  nowDate,
  selectedTime,
  setSelectedTime,
  selectedStaff,
  setSelectedStaff
}) => {
  const containerRef = useRef(null);
  const timeSectionRef = useRef(null);

  // state های اصلی
  // const [selectedTime, setSelectedTime] = useState(null);
  // const [selectedStaff, setSelectedStaff] = useState(null);

  // state دریافت جزئیات روز
  const [dayDetail, setDayDetail] = useState(null);
  const [fetchingDay, setFetchingDay] = useState(false);
  
  // cutoff time for today (HH:MM string) – null if not applicable
  const [todayCutoffTime, setTodayCutoffTime] = useState(null);

  // state های تقویم
  const serverToday =
    availabilityData?.dateNowServer?.today || moment().format("YYYY-MM-DD");
  const initialMoment = moment(serverToday, "YYYY-MM-DD");
  const [selectedYear, setSelectedYear] = useState(initialMoment.jYear());
  const [selectedMonth, setSelectedMonth] = useState(initialMoment.jMonth() + 1);

  // ============= اسکرول به بالا هنگام mount =============
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // ============= نقشه‌های تقویم =============
  const availableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.availableDates || []).forEach((item) => {
      map[item.date] = item;
    });
    return map;
  }, [availabilityData]);

  const unavailableMap = useMemo(() => {
    const map = {};
    (availabilityData?.availability?.unavailableDates || []).forEach((item) => {
      map[item.date] = item;
    });
    return map;
  }, [availabilityData]);

  const currentMonthName =
    selectedMonth >= 1 && selectedMonth <= 12
      ? persianMonths[selectedMonth - 1].name
      : "نامشخص";

  // ============= تغییر ماه =============
  const handleChangeMonth = useCallback((direction) => {
    if (direction === "next") {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(prev => prev + 1);
      } else {
        setSelectedMonth(prev => prev + 1);
      }
    } else {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(prev => prev - 1);
      } else {
        setSelectedMonth(prev => prev - 1);
      }
    }
  }, [selectedMonth]);

  // ============= ساخت خانه‌های تقویم =============
  const calendarDays = useMemo(() => {
    if (!selectedMonth || !selectedYear) return [];
    
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(
      `${selectedYear}/${selectedMonth}/1`,
      "jYYYY/jM/jD"
    );
    const startDayOfWeek = firstDayOfMonth.day() + 1;
    const days = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(
        `${selectedYear}/${selectedMonth}/${day}`,
        "jYYYY/jM/jD"
      );
      const gregorianDate = jalaaliDate.format("YYYY-MM-DD");
      const availableItem = availableMap[gregorianDate];
      const unavailableItem = unavailableMap[gregorianDate];

      days.push({
        day,
        jalaaliDate: jalaaliDate.format("jYYYY/jMM/jDD"),
        gregorianDate,
        isToday: gregorianDate === serverToday,
        isAvailable: Boolean(availableItem),
        isUnavailable: Boolean(unavailableItem),
        isSelected: internalSelectedDate?.date === gregorianDate,
        availableItem,
        unavailableItem,
      });
    }
    return days;
  }, [selectedMonth, selectedYear, availableMap, unavailableMap, serverToday, internalSelectedDate]);

  // ============= انتخاب روز =============
  const handleSelectDay = useCallback((dateObj) => {
    if (!dateObj || !dateObj.isAvailable) return;

    const datePayload = {
      date: dateObj.gregorianDate,
      jalaaliDate: dateObj.jalaaliDate,
    };
    setInternalSelectedDate(datePayload);
    setSelectedTime(null);
    setSelectedStaff(null);
    setDayDetail(null);
    setTodayCutoffTime(null); // reset cutoff

    setTimeout(() => {
      if (timeSectionRef.current) {
        timeSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  }, []);

  // ============= دریافت جزئیات روز از API =============
  useEffect(() => {
    if (!internalSelectedDate || !axiosAdmin || !serviceId || !type) {
      setDayDetail(null);
      setTodayCutoffTime(null);
      return;
    }

    const fetchDay = async () => {
      setFetchingDay(true);
      try {
        const res = await axiosAdmin.get("/api/user/availability/day/normal", {
          params: {
            serviceId,
            type,
            date: internalSelectedDate.date,
          },
        });
        
        // بررسی آیا تاریخ انتخاب شده امروز است
        const selectedDate = internalSelectedDate.date; // "2026-06-23"
        const serverDateStr = nowDate ? nowDate.split('T')[0] : null; // "2026-06-23"
        
        // اگر تاریخ انتخاب شده با تاریخ سرور برابر باشد (امروز)
        if (selectedDate === serverDateStr && nowDate) {
          // استخراج زمان از nowDate (مثال: "2026-06-23T10:30:25.900Z")
          const timePart = nowDate.split('T')[1]; // "10:30:25.900Z"
          const timeWithoutSeconds = timePart ? timePart.split(':').slice(0, 2).join(':') : null; // "10:30"
          
          if (timeWithoutSeconds) {
            // اضافه کردن ۳ ساعت و ۳۰ دقیقه
            const cutoffTime = addTime(timeWithoutSeconds, 3, 30);
            setTodayCutoffTime(cutoffTime);
          }
        } else {
          setTodayCutoffTime(null);
        }
        
        setDayDetail(res.data);
      } catch (err) {
        console.error("Error fetching normal day availability:", err);
        toast.error("خطا در دریافت جزئیات روز");
        setDayDetail(null);
        setTodayCutoffTime(null);
      } finally {
        setFetchingDay(false);
      }
    };
    
    fetchDay();
  }, [internalSelectedDate, axiosAdmin, serviceId, type]);

  // ============= انتخاب تایم (انتخاب رندوم نیرو) =============
  const handleSelectTime = useCallback((time) => {
    if (!dayDetail?.timeSlots) return;
    
    // بررسی اگر تایم گذشته باشد (برای امروز)
    const selectedDate = internalSelectedDate?.date;
    const serverDateStr = nowDate ? nowDate.split('T')[0] : null;
    
    if (selectedDate === serverDateStr && todayCutoffTime) {
      // اگر امروز است و تایم قبل از cutoffTime باشد، انتخاب نشود
      if (time < todayCutoffTime) {
        return;
      }
    }
    
    const slot = dayDetail.timeSlots.find((s) => s.time === time);
    if (!slot || !slot.available) return;

    setSelectedTime(time);
    const staffs = slot.staffs || [];
    const pickedStaff = pickRandomStaffWithLowestLoad(staffs);
    setSelectedStaff(pickedStaff);
  }, [dayDetail, internalSelectedDate, nowDate, todayCutoffTime]);

  // ============= دلیل غیرقابل رزرو بودن =============
  const getUnavailableReasonText = useCallback((dateObj) => {
    const reasons = dateObj?.unavailableItem?.reasons || [];
    if (!reasons.length) return "غیرقابل رزرو";
    return reasons
      .map((r) => reasonLabelMap[r.code] || r.message)
      .join("، ");
  }, []);

  // ============= فیلتر تایم‌های قابل نمایش =============
  const filteredAvailableTimes = useMemo(() => {
    if (!dayDetail?.availableTimes) return [];
    
    const selectedDate = internalSelectedDate?.date;
    const serverDateStr = nowDate ? nowDate.split('T')[0] : null;
    
    // اگر تاریخ انتخاب شده امروز نیست یا cutoffTime نداریم، همه تایم‌ها را برگردان
    if (selectedDate !== serverDateStr || !todayCutoffTime) {
      return dayDetail.availableTimes;
    }
    
    // برای امروز: فقط تایم‌های بعد از cutoffTime را برگردان
    return dayDetail.availableTimes.filter(time => time >= todayCutoffTime);
  }, [dayDetail, internalSelectedDate, nowDate, todayCutoffTime]);

  // ============= بررسی اینکه آیا تایم گذشته است (برای نمایش) =============
  const isTimePast = useCallback((time) => {
    const selectedDate = internalSelectedDate?.date;
    const serverDateStr = nowDate ? nowDate.split('T')[0] : null;
    
    if (selectedDate === serverDateStr && todayCutoffTime) {
      return time < todayCutoffTime;
    }
    return false;
  }, [internalSelectedDate, nowDate, todayCutoffTime]);

  // ============= رندر =============
  return (
    <div ref={containerRef} className="space-y-6">
      {/* ========== تقویم ========== */}
      <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {/* هدر تقویم */}
        <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => handleChangeMonth("prev")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <h3 className="text-lg md:text-xl font-black text-white">
              {currentMonthName} {selectedYear}
            </h3>
            <p className="text-[10px] md:text-xs text-slate-300 mt-1">
              {isLeapYear(selectedYear) ? "سال کبیسه" : "سال عادی"}
            </p>
          </div>

          <button
            type="button"
            onClick={() => handleChangeMonth("next")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* روزهای هفته */}
        <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {weekDays.map((day) => (
            <div
              key={day}
              className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600"
            >
              {day}
            </div>
          ))}
        </div>

        {/* شبکه روزها */}
        <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
          {calendarDays.map((dateObj, index) => {
            if (!dateObj) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const disabled = !dateObj.isAvailable;

            return (
              <button
                key={dateObj.gregorianDate}
                type="button"
                disabled={disabled}
                onClick={() => handleSelectDay(dateObj)}
                title={
                  disabled
                    ? getUnavailableReasonText(dateObj)
                    : "قابل رزرو"
                }
                className={`
                  relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 
                  flex flex-col items-center justify-center gap-0.5
                  text-[12px] md:text-base font-black
                  ${
                    dateObj.isSelected
                      ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
                      : dateObj.isAvailable
                      ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
                      : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
                  }
                `}
              >
                {/* نشانگر امروز */}
                {dateObj.isToday && (
                  <span
                    className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                      dateObj.isSelected ? "bg-white" : "bg-blue-500"
                    }`}
                  />
                )}

                {/* قفل برای روزهای غیرقابل رزرو */}
                {disabled && (
                  <span className="absolute top-1 left-1">
                    <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
                  </span>
                )}

                {/* تیک برای روزهای قابل رزرو (دسکتاپ) */}
                {!disabled && !dateObj.isSelected && (
                  <span className="absolute top-1 left-1 hidden md:block">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  </span>
                )}

                <span className="text-sm md:text-lg">{dateObj.day}</span>

                {/* دلیل عدم دسترسی (دسکتاپ) */}
                {!dateObj.isAvailable && (
                  <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
                    {getUnavailableReasonText(dateObj)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ========== بخش انتخاب زمان ========== */}
      {internalSelectedDate && (
        <div ref={timeSectionRef} className="space-y-4">
          {/* نمایش تاریخ انتخاب‌شده */}
          <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-purple-800">
                تاریخ انتخاب‌شده: {internalSelectedDate.jalaaliDate}
              </p>
            </div>
          </div>

          {/* نمایش وضعیت‌های مختلف */}
          {fetchingDay ? (
            <div className="min-h-[200px] flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-9 h-9 text-purple-600 animate-spin" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  در حال دریافت ساعات
                </h3>
                <p className="text-sm text-slate-500">لطفاً چند لحظه صبر کنید...</p>
              </div>
            </div>
          ) : dayDetail ? (
            dayDetail.isAvailable ? (
              <div>
                <h4 className="text-sm font-bold text-slate-700 mb-3">
                  یک زمان برای رزرو نوبت خود انتخاب کنید
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {filteredAvailableTimes && filteredAvailableTimes.length > 0 ? (
                    filteredAvailableTimes.map((time) => {
                      const isSelected = selectedTime === time;
                      const isPast = isTimePast(time);
                      const isDisabled = isPast;
                      
                      // پیدا کردن تعداد نیروهای این تایم
                      const slot = dayDetail.timeSlots?.find(
                        (s) => s.time === time
                      );
                      const staffCount = slot?.staffs?.length || 0;

                      // اگر تایم گذشته باشد (برای امروز)
                      if (isPast) {
                        return (
                          <div
                            key={time}
                            className="relative rounded-xl border border-slate-200 bg-slate-100 p-2 text-center opacity-60 cursor-not-allowed select-none"
                          >
                            <span className="text-sm font-semibold text-slate-400 line-through">
                              {time}
                            </span>
                            <div className="text-[10px] text-red-400 mt-0.5">
                              گذشت
                            </div>
                          </div>
                        );
                      }

                      return (
                        <button
                          key={time}
                          type="button"
                          onClick={() => handleSelectTime(time)}
                          className={`
                            relative rounded-xl border-2 p-2 text-center transition-all duration-200
                            ${
                              isSelected
                                ? "border-purple-600 bg-purple-50 shadow-md shadow-purple-200"
                                : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
                            }
                          `}
                        >
                          <span
                            className={`text-sm font-bold ${
                              isSelected ? "text-purple-700" : "text-slate-800"
                            }`}
                          >
                            {time}
                          </span>
                          {/* نمایش تعداد نیروهای آزاد */}
                          {/* <div className="flex items-center justify-center gap-1 mt-0.5">
                            <Users className="w-3 h-3 text-slate-400" />
                            <span className="text-[10px] text-slate-500">
                              {staffCount} نیرو
                            </span>
                          </div> */}
                          {isSelected && (
                            <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="col-span-full text-center py-8 text-slate-500">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm font-medium">
                        {todayCutoffTime 
                          ? "هیچ زمان آزادی برای امروز بعد از این ساعت وجود ندارد" 
                          : "هیچ زمان آزادی برای این روز وجود ندارد"}
                      </p>
                    </div>
                  )}
                </div>

                {/* نمایش نیروی انتخاب‌شده */}
                {/* {selectedStaff && selectedTime && (
                  <div className="mt-5 flex items-center gap-3 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                    <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-purple-900">
                        {selectedStaff.displayName}
                      </p>
                      <p className="text-xs text-purple-600 mt-0.5">
                        <span className="font-medium">تعداد قرارهای فعال:</span>{" "}
                        {selectedStaff.appointmentCount || 0}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        <Clock className="w-3 h-3 inline ml-1" />
                        {selectedTime}
                      </p>
                    </div>
                  </div>
                )} */}
              </div>
            ) : (
              <div className="text-center py-8 bg-red-50 border border-red-200 rounded-2xl">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h4 className="text-sm font-bold text-red-700 mb-1">
                  این روز قابل رزرو نیست
                </h4>
                <p className="text-xs text-red-600">
                  {dayDetail.reasons?.map(r => reasonLabelMap[r.code] || r.message).join("، ") || "غیرقابل رزرو"}
                </p>
              </div>
            )
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p>برای مشاهده زمان‌های آزاد، یک روز را انتخاب کنید</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddAppointmentContentStep2Normal;


// import { useState, useEffect, useMemo, useRef, useCallback } from "react";
// import moment from "moment-jalaali";
// import {
//   Calendar,
//   ChevronLeft,
//   ChevronRight,
//   Lock,
//   CheckCircle,
//   Loader2,
//   AlertCircle,
//   User,
//   Users,
//   Clock,
//   Clover,
// } from "lucide-react";
// import { toast } from "react-toastify";

// // ============= ثابت‌ها =============
// const persianMonths = [
//   { id: 1, name: "فروردین", days: 31 },
//   { id: 2, name: "اردیبهشت", days: 31 },
//   { id: 3, name: "خرداد", days: 31 },
//   { id: 4, name: "تیر", days: 31 },
//   { id: 5, name: "مرداد", days: 31 },
//   { id: 6, name: "شهریور", days: 31 },
//   { id: 7, name: "مهر", days: 30 },
//   { id: 8, name: "آبان", days: 30 },
//   { id: 9, name: "آذر", days: 30 },
//   { id: 10, name: "دی", days: 30 },
//   { id: 11, name: "بهمن", days: 30 },
//   { id: 12, name: "اسفند", days: 29 },
// ];

// const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

// const reasonLabelMap = {
//   SALON_HOLIDAY: "تعطیلی سالن",
//   FULLY_BOOKED: "ظرفیت تکمیل",
//   NO_STAFF: "هیچ نیرویی موجود نیست",
//   NO_STAFF_WORKING: "هیچ نیرویی کار نمی‌کند",
// };

// const isLeapYear = (year) => {
//   const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
//   return breaks.includes(year % 33);
// };

// const getDaysInMonth = (month, year) => {
//   if (!month || month < 1 || month > 12) return 30;
//   if (month === 12 && isLeapYear(year)) return 30;
//   return persianMonths[month - 1].days;
// };

// // ============= تابع انتخاب رندوم نیرو با اولویت کمترین قرار =============
// const pickRandomStaffWithLowestLoad = (staffs) => {
//   if (!staffs || staffs.length === 0) return null;
//   if (staffs.length === 1) return staffs[0];

//   // پیدا کردن کمترین appointmentCount
//   const minCount = Math.min(...staffs.map((s) => s.appointmentCount ?? 0));
//   // فیلتر کردن نیروهایی که کمترین قرار را دارند
//   const leastLoaded = staffs.filter(
//     (s) => (s.appointmentCount ?? 0) === minCount
//   );
//   // انتخاب رندوم از بین آن‌ها
//   const randomIndex = Math.floor(Math.random() * leastLoaded.length);
//   return leastLoaded[randomIndex];
// };

// // ============= کامپوننت اصلی =============
// const AddAppointmentContentStep2Normal = ({
//   availabilityData,
//   axiosAdmin,
//   serviceId,
//   type,
//   onStepDataChange,

//   internalSelectedDate,
//   setInternalSelectedDate,
//   nowDate
// }) => {
//   const containerRef = useRef(null);
//   const timeSectionRef = useRef(null);

//   // state های اصلی
//   // const [internalSelectedDate, setInternalSelectedDate] = useState(null);
//   const [selectedTime, setSelectedTime] = useState(null);
//   const [selectedStaff, setSelectedStaff] = useState(null);

//   // state دریافت جزئیات روز
//   const [dayDetail, setDayDetail] = useState(null);
//   const [fetchingDay, setFetchingDay] = useState(false);

//   // state های تقویم
//   const serverToday =
//     availabilityData?.dateNowServer?.today || moment().format("YYYY-MM-DD");
//   const initialMoment = moment(serverToday, "YYYY-MM-DD");
//   const [selectedYear, setSelectedYear] = useState(initialMoment.jYear());
//   const [selectedMonth, setSelectedMonth] = useState(initialMoment.jMonth() + 1);

//   // ============= اسکرول به بالا هنگام mount =============
//   useEffect(() => {
//     const timer = setTimeout(() => {
//       if (containerRef.current) {
//         containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
//       } else {
//         window.scrollTo({ top: 0, behavior: "smooth" });
//       }
//     }, 100);
//     return () => clearTimeout(timer);
//   }, []);

//   // ============= نقشه‌های تقویم =============
//   const availableMap = useMemo(() => {
//     const map = {};
//     (availabilityData?.availability?.availableDates || []).forEach((item) => {
//       map[item.date] = item;
//     });
//     return map;
//   }, [availabilityData]);

//   const unavailableMap = useMemo(() => {
//     const map = {};
//     (availabilityData?.availability?.unavailableDates || []).forEach((item) => {
//       map[item.date] = item;
//     });
//     return map;
//   }, [availabilityData]);

//   const currentMonthName =
//     selectedMonth >= 1 && selectedMonth <= 12
//       ? persianMonths[selectedMonth - 1].name
//       : "نامشخص";

//   // ============= تغییر ماه =============
//   const changeMonth = useCallback((direction) => {
//     setSelectedYear(prevYear => {
//       setSelectedMonth(prevMonth => {
//         if (direction === "next") {
//           if (prevMonth === 12) {
//             return { newMonth: 1, newYear: prevYear + 1 };
//           }
//           return { newMonth: prevMonth + 1, newYear: prevYear };
//         } else {
//           if (prevMonth === 1) {
//             return { newMonth: 12, newYear: prevYear - 1 };
//           }
//           return { newMonth: prevMonth - 1, newYear: prevYear };
//         }
//       });
//       return prevYear; // این مقدار موقتی است، مقدار واقعی در setSelectedMonth تنظیم می‌شود
//     });
//   }, []);

//   // اصلاح تابع changeMonth برای جلوگیری از حلقه
//   const handleChangeMonth = useCallback((direction) => {
//     if (direction === "next") {
//       if (selectedMonth === 12) {
//         setSelectedMonth(1);
//         setSelectedYear(prev => prev + 1);
//       } else {
//         setSelectedMonth(prev => prev + 1);
//       }
//     } else {
//       if (selectedMonth === 1) {
//         setSelectedMonth(12);
//         setSelectedYear(prev => prev - 1);
//       } else {
//         setSelectedMonth(prev => prev - 1);
//       }
//     }
//   }, [selectedMonth]);

//   // ============= ساخت خانه‌های تقویم =============
//   const calendarDays = useMemo(() => {
//     if (!selectedMonth || !selectedYear) return [];
    
//     const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
//     const firstDayOfMonth = moment(
//       `${selectedYear}/${selectedMonth}/1`,
//       "jYYYY/jM/jD"
//     );
//     const startDayOfWeek = firstDayOfMonth.day() + 1;
//     const days = [];

//     for (let i = 0; i < startDayOfWeek; i++) {
//       days.push(null);
//     }

//     for (let day = 1; day <= daysInMonth; day++) {
//       const jalaaliDate = moment(
//         `${selectedYear}/${selectedMonth}/${day}`,
//         "jYYYY/jM/jD"
//       );
//       const gregorianDate = jalaaliDate.format("YYYY-MM-DD");
//       const availableItem = availableMap[gregorianDate];
//       const unavailableItem = unavailableMap[gregorianDate];

//       days.push({
//         day,
//         jalaaliDate: jalaaliDate.format("jYYYY/jMM/jDD"),
//         gregorianDate,
//         isToday: gregorianDate === serverToday,
//         isAvailable: Boolean(availableItem),
//         isUnavailable: Boolean(unavailableItem),
//         isSelected: internalSelectedDate?.date === gregorianDate,
//         availableItem,
//         unavailableItem,
//       });
//     }
//     return days;
//   }, [selectedMonth, selectedYear, availableMap, unavailableMap, serverToday, internalSelectedDate]);

//   // ============= انتخاب روز =============
//   const handleSelectDay = useCallback((dateObj) => {
//     if (!dateObj || !dateObj.isAvailable) return;

//     const datePayload = {
//       date: dateObj.gregorianDate,
//       jalaaliDate: dateObj.jalaaliDate,
//     };
//     setInternalSelectedDate(datePayload);
//     setSelectedTime(null);
//     setSelectedStaff(null);
//     setDayDetail(null);

//     setTimeout(() => {
//       if (timeSectionRef.current) {
//         timeSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
//       }
//     }, 100);
//   }, []);

//   // ============= دریافت جزئیات روز از API =============
//   useEffect(() => {
//     if (!internalSelectedDate || !axiosAdmin || !serviceId || !type) {
//       setDayDetail(null);
//       return;
//     }

//     const fetchDay = async () => {
//       setFetchingDay(true);
//       try {
//         const res = await axiosAdmin.get("/api/user/availability/day/normal", {
//           params: {
//             serviceId,
//             type,
//             date: internalSelectedDate.date,
//           },
//         });
//         setDayDetail(res.data);
//       } catch (err) {
//         console.error("Error fetching normal day availability:", err);
//         toast.error("خطا در دریافت جزئیات روز");
//         setDayDetail(null);
//       } finally {
//         setFetchingDay(false);
//       }
//     };
    
//     fetchDay();
//   }, [internalSelectedDate, axiosAdmin, serviceId, type]);

//   // ============= انتخاب تایم (انتخاب رندوم نیرو) =============
//   const handleSelectTime = useCallback((time) => {
//     if (!dayDetail?.timeSlots) return;
    
//     const slot = dayDetail.timeSlots.find((s) => s.time === time);
//     if (!slot || !slot.available) return;

//     setSelectedTime(time);
//     const staffs = slot.staffs || [];
//     const pickedStaff = pickRandomStaffWithLowestLoad(staffs);
//     setSelectedStaff(pickedStaff);
//   }, [dayDetail]);

//   // ============= دلیل غیرقابل رزرو بودن =============
//   const getUnavailableReasonText = useCallback((dateObj) => {
//     const reasons = dateObj?.unavailableItem?.reasons || [];
//     if (!reasons.length) return "غیرقابل رزرو";
//     return reasons
//       .map((r) => reasonLabelMap[r.code] || r.message)
//       .join("، ");
//   }, []);

//   // ============= ارسال داده به والد =============
//   // useEffect(() => {
//   //   if (onStepDataChange) {
//   //     onStepDataChange({
//   //       date: internalSelectedDate,
//   //       time: selectedTime,
//   //       staff: selectedStaff,
//   //       isComplete: !!(internalSelectedDate && selectedTime && selectedStaff),
//   //     });
//   //   }
//   // }, [internalSelectedDate, selectedTime, selectedStaff, onStepDataChange]);

//   // ============= رندر =============
//   return (
//     <div ref={containerRef} className="space-y-6">
//       {/* ========== تقویم ========== */}
//       <div className="bg-white max-w-2xl mx-auto border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
//         {/* هدر تقویم */}
//         <div className="bg-slate-900 px-4 md:px-5 py-4 flex items-center justify-between">
//           <button
//             type="button"
//             onClick={() => handleChangeMonth("prev")}
//             className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
//           >
//             <ChevronRight className="w-5 h-5 text-white" />
//           </button>

//           <div className="text-center">
//             <h3 className="text-lg md:text-xl font-black text-white">
//               {currentMonthName} {selectedYear}
//             </h3>
//             <p className="text-[10px] md:text-xs text-slate-300 mt-1">
//               {isLeapYear(selectedYear) ? "سال کبیسه" : "سال عادی"}
//             </p>
//           </div>

//           <button
//             type="button"
//             onClick={() => handleChangeMonth("next")}
//             className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
//           >
//             <ChevronLeft className="w-5 h-5 text-white" />
//           </button>
//         </div>

//         {/* روزهای هفته */}
//         <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
//           {weekDays.map((day) => (
//             <div
//               key={day}
//               className="py-2 md:py-3 text-center text-xs md:text-sm font-bold text-slate-600"
//             >
//               {day}
//             </div>
//           ))}
//         </div>

//         {/* شبکه روزها */}
//         <div className="grid grid-cols-7 gap-1 md:gap-2 p-2 md:p-4 bg-white">
//           {calendarDays.map((dateObj, index) => {
//             if (!dateObj) {
//               return <div key={`empty-${index}`} className="aspect-square" />;
//             }

//             const disabled = !dateObj.isAvailable;

//             return (
//               <button
//                 key={dateObj.gregorianDate}
//                 type="button"
//                 disabled={disabled}
//                 onClick={() => handleSelectDay(dateObj)}
//                 title={
//                   disabled
//                     ? getUnavailableReasonText(dateObj)
//                     : "قابل رزرو"
//                 }
//                 className={`
//                   relative aspect-square rounded-xl md:rounded-2xl border transition-all duration-300 
//                   flex flex-col items-center justify-center gap-0.5
//                   text-[12px] md:text-base font-black
//                   ${
//                     dateObj.isSelected
//                       ? "border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105"
//                       : dateObj.isAvailable
//                       ? "border-green-200 bg-white hover:bg-green-50 hover:border-green-400 hover:shadow-md cursor-pointer"
//                       : "border-slate-200 bg-slate-100 text-slate-400 opacity-60 cursor-not-allowed"
//                   }
//                 `}
//               >
//                 {/* نشانگر امروز */}
//                 {dateObj.isToday && (
//                   <span
//                     className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
//                       dateObj.isSelected ? "bg-white" : "bg-blue-500"
//                     }`}
//                   />
//                 )}

//                 {/* قفل برای روزهای غیرقابل رزرو */}
//                 {disabled && (
//                   <span className="absolute top-1 left-1">
//                     <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-slate-400" />
//                   </span>
//                 )}

//                 {/* تیک برای روزهای قابل رزرو (دسکتاپ) */}
//                 {!disabled && !dateObj.isSelected && (
//                   <span className="absolute top-1 left-1 hidden md:block">
//                     <CheckCircle className="w-3.5 h-3.5 text-green-500" />
//                   </span>
//                 )}

//                 <span className="text-sm md:text-lg">{dateObj.day}</span>

//                 {/* دلیل عدم دسترسی (دسکتاپ) */}
//                 {!dateObj.isAvailable && (
//                   <span className="hidden md:inline text-[9px] md:text-[10px] px-0.5 text-center leading-4 line-clamp-2">
//                     {getUnavailableReasonText(dateObj)}
//                   </span>
//                 )}
//               </button>
//             );
//           })}
//         </div>
//       </div>

//       {/* ========== بخش انتخاب زمان ========== */}
//       {internalSelectedDate && (
//         <div ref={timeSectionRef} className="space-y-4">
//           {/* نمایش تاریخ انتخاب‌شده */}
//           <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-2xl p-4">
//             <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
//             <div className="min-w-0">
//               <p className="text-sm font-semibold text-purple-800">
//                 تاریخ انتخاب‌شده: {internalSelectedDate.jalaaliDate}
//               </p>
//             </div>
//           </div>

//           {/* نمایش وضعیت‌های مختلف */}
//           {fetchingDay ? (
//             <div className="min-h-[200px] flex items-center justify-center">
//               <div className="text-center">
//                 <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
//                   <Loader2 className="w-9 h-9 text-purple-600 animate-spin" />
//                 </div>
//                 <h3 className="text-lg font-bold text-slate-800 mb-2">
//                   در حال دریافت ساعات
//                 </h3>
//                 <p className="text-sm text-slate-500">لطفاً چند لحظه صبر کنید...</p>
//               </div>
//             </div>
//           ) : dayDetail ? (
//             dayDetail.isAvailable ? (
//               <div>
//                 <h4 className="text-sm font-bold text-slate-700 mb-3">
//                   یک زمان برای رزرو نوبت خود انتخاب کنید
//                 </h4>
//                 <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
//                   {dayDetail.availableTimes && dayDetail.availableTimes.length > 0 ? (
//                     dayDetail.availableTimes.map((time) => {
//                       const isSelected = selectedTime === time;
//                       // پیدا کردن تعداد نیروهای این تایم
//                       const slot = dayDetail.timeSlots?.find(
//                         (s) => s.time === time
//                       );
//                       const staffCount = slot?.staffs?.length || 0;

//                       return (
//                         <button
//                           key={time}
//                           type="button"
//                           onClick={() => handleSelectTime(time)}
//                           className={`
//                             relative rounded-xl border-2 p-2 text-center transition-all duration-200
//                             ${
//                               isSelected
//                                 ? "border-purple-600 bg-purple-50 shadow-md shadow-purple-200"
//                                 : "border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm"
//                             }
//                           `}
//                         >
//                           <span
//                             className={`text-sm font-bold ${
//                               isSelected ? "text-purple-700" : "text-slate-800"
//                             }`}
//                           >
//                             {time}
//                           </span>
//                           {/* نمایش تعداد نیروهای آزاد */}
//                           {/* <div className="flex items-center justify-center gap-1 mt-0.5">
//                             <Users className="w-3 h-3 text-slate-400" />
//                             <span className="text-[10px] text-slate-500">
//                               {staffCount} نیرو
//                             </span>
//                           </div> */}
//                           {isSelected && (
//                             <span className="absolute -top-1.5 -right-1.5 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
//                               ✓
//                             </span>
//                           )}
//                         </button>
//                       );
//                     })
//                   ) : (
//                     <div className="col-span-full text-center py-8 text-slate-500">
//                       <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
//                       <p className="text-sm font-medium">هیچ زمان آزادی برای این روز وجود ندارد</p>
//                     </div>
//                   )}
//                 </div>

//                 {/* نمایش نیروی انتخاب‌شده */}
//                 {/* {selectedStaff && selectedTime && (
//                   <div className="mt-5 flex items-center gap-3 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
//                     <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
//                       <User className="w-6 h-6 text-purple-600" />
//                     </div>
//                     <div className="min-w-0">
//                       <p className="text-sm font-bold text-purple-900">
//                         {selectedStaff.displayName}
//                       </p>
//                       <p className="text-xs text-purple-600 mt-0.5">
//                         <span className="font-medium">تعداد قرارهای فعال:</span>{" "}
//                         {selectedStaff.appointmentCount || 0}
//                       </p>
//                       <p className="text-xs text-slate-500 mt-1">
//                         <Clock className="w-3 h-3 inline ml-1" />
//                         {selectedTime}
//                       </p>
//                     </div>
//                   </div>
//                 )} */}
//               </div>
//             ) : (
//               <div className="text-center py-8 bg-red-50 border border-red-200 rounded-2xl">
//                 <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
//                 <h4 className="text-sm font-bold text-red-700 mb-1">
//                   این روز قابل رزرو نیست
//                 </h4>
//                 <p className="text-xs text-red-600">
//                   {dayDetail.reasons?.map(r => reasonLabelMap[r.code] || r.message).join("، ") || "غیرقابل رزرو"}
//                 </p>
//               </div>
//             )
//           ) : (
//             <div className="text-center py-8 text-slate-500">
//               <p>برای مشاهده زمان‌های آزاد، یک روز را انتخاب کنید</p>
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// export default AddAppointmentContentStep2Normal;